import { Box, Stack, Typography, theme } from '@dtx-company/design-system/src'
import { EditTheme } from './types'
import { FC } from 'react'
import { InterestsOutlined } from '@mui/icons-material'
import {
  LINK_SHAPE_LABELS,
  LINK_STYLE_LABELS,
  LinkShape,
  LinkStyle
} from '../../../constants/customizeTheme'
import { LabelText } from './subcomponents'
import { SelectorFrame } from './SelectorFrame'
import { stopPropagationEvents } from '../PageEditor/components/LinkEditDrawer/LinkEditDrawer.utils'
import { useCurrentPagePermissions } from '../../../utils/useGetCurrentPagePermissions'
import { useLinkStyleOptions } from './LinkStyle.hooks'
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'
import { ButtonBase } from '@mui/material'

interface LinkStyleCardProps {
  id?: string
  handleSelect: (values: EditTheme, reset?: boolean) => void
  currShape?: string
  currStyle?: string
  disabled?: boolean
}

const linkShapeStyles = {
  [LinkShape.DEFAULT]: {
    borderRadius: 3
  },
  [LinkShape.ROUND]: {
    borderRadius: 10
  },
  [LinkShape.SQUARE]: {
    borderRadius: 0
  }
}

const linkStyleStyles = {
  [LinkStyle.DEFAULT]: {
    border: `${theme.spacing(0.5)} solid #DEDFE0`,
    boxShadow: theme.shadows[1]
  },
  [LinkStyle.OUTLINED]: {
    border: `${theme.spacing(0.5)} solid #5B5E63`
  },
  [LinkStyle.OUTLINED_BW]: {
    border: `${theme.spacing(0.5)} solid #5B5E63`
  },
  [LinkStyle.INSET]: {
    border: `${theme.spacing(0.5)} solid #5B5E63`,
    boxShadow: 'rgb(0 0 0 / 35%) 7px 7px 0px 0px'
  },
  [LinkStyle.COLORED]: {
    border: `${theme.spacing(0.5)} solid #5B5E63`,
    background: '#5B5E63'
  },
  [LinkStyle.GRADIENT]: {
    background: `linear-gradient(358.57deg, rgba(173, 173, 173, 0.8) 6.45%, rgba(196, 196, 196, 0.2) 98.84%)`
  }
}

export const LinkShapePicker: FC<LinkStyleCardProps> = ({
  handleSelect,
  currShape,
  disabled: d
}) => {
  const { hasViewOnlyAccess } = useCurrentPagePermissions()
  const disabled = hasViewOnlyAccess || d
  return (
    <Stack spacing={4}>
      <Stack sx={{ mb: { xs: 2, lg: 4 } }} direction="row" alignItems="center" spacing={{ md: 2 }}>
        <InterestsOutlined sx={{ display: { xs: 'none', md: 'flex' }, fontSize: 16 }} />
        <Typography variant={{ xs: 'bodyMedium', md: 'subtitleSmall' }}>
          Choose a link shape
        </Typography>
      </Stack>
      <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {Object.values(LinkShape).map(shape => {
          const selected = currShape === shape
          return (
            <ButtonBase
              data-testid={shape}
              key={shape}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
              onClick={() => {
                handleSelect({ linkShape: shape })
                events.Flowpage_PageProfile_Selected_Shape(shape)
              }}
              disabled={disabled}
              {...stopPropagationEvents}
            >
              <Box
                sx={{
                  display: 'flex',
                  position: 'relative',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  width: { xs: theme.spacing(25), lg: theme.spacing(37.5) },
                  height: { xs: theme.spacing(12), lg: theme.spacing(17.5) },
                  borderRadius: theme.spacing(linkShapeStyles[shape].borderRadius),
                  backgroundColor: { xs: '#ADADAD', lg: '#5B5E63' },
                  m: theme.spacing(0, 2, 4)
                }}
              >
                {selected && <SelectorFrame />}
              </Box>

              <LabelText
                display={['none', 'none', 'flex']}
                selected={selected}
                opacity={disabled ? 0.35 : 1}
                label={LINK_SHAPE_LABELS[shape]}
              />
            </ButtonBase>
          )
        })}
      </Stack>
    </Stack>
  )
}

export const LinkStylePicker: FC<LinkStyleCardProps> = ({ handleSelect, currStyle, disabled }) => {
  const { linkStylesOptions, selectedLinkStyle, handleLinkStyleChange, canUserEditLinkStyle } =
    useLinkStyleOptions({
      handleSelect,
      currStyle
    })
  const isDisabled = !canUserEditLinkStyle || disabled
  return (
    <Stack spacing={4}>
      <Stack
        sx={{
          mb: { xs: 2, lg: 4 },
          opacity: isDisabled ? 0.35 : 1
        }}
        direction="row"
        alignItems="center"
        spacing={{ md: 2 }}
      >
        <StyleOutlinedIcon />
        <Typography variant={{ xs: 'bodyMedium', md: 'subtitleSmall' }}>
          Choose a link style
        </Typography>
      </Stack>

      <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {linkStylesOptions.map((styleOption, i) => {
          const selected = selectedLinkStyle === styleOption
          return (
            <Box
              {...stopPropagationEvents}
              key={styleOption + i}
              sx={{
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}
              onClick={() => {
                !isDisabled && handleLinkStyleChange(styleOption)
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  position: 'relative',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isDisabled ? 0.35 : 1,
                  cursor: isDisabled ? 'default' : 'pointer',
                  width: { xs: theme.spacing(25), md: theme.spacing(37.5) },
                  height: { xs: theme.spacing(12), md: theme.spacing(17.5) },
                  m: theme.spacing(1, 2, 2),
                  borderRadius: theme.spacing(3),
                  ...linkStyleStyles[styleOption]
                }}
              >
                {styleOption === LinkStyle.OUTLINED_BW && (
                  <Typography variant="labelSmall" color="text.secondary" align="center">
                    {'B/W'}
                  </Typography>
                )}
                {selected && <SelectorFrame />}
              </Box>
              <LabelText
                display={['none', 'none', 'flex']}
                selected={selected}
                opacity={isDisabled ? 0.35 : 1}
                label={LINK_STYLE_LABELS[styleOption]}
              />
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}

export const LinkStyleCard: FC<LinkStyleCardProps> = ({
  handleSelect,
  currStyle,
  currShape,
  disabled
}) => {
  return (
    <Stack spacing={5}>
      <LinkShapePicker disabled={disabled} handleSelect={handleSelect} currShape={currShape} />
      <LinkStylePicker disabled={disabled} handleSelect={handleSelect} currStyle={currStyle} />
    </Stack>
  )
}
